'use client';

import { useEffect } from 'react';

export default function ChunkLoadErrorHandler() {
    useEffect(() => {
        const getErrorMessage = (error: unknown) => {
            if (!error) return '';
            if (typeof error === 'string') return error.toLowerCase();
            if (error instanceof Error) {
                return `${error.name} ${error.message}`.toLowerCase();
            }
            if (typeof error === 'object') {
                const candidate = error as { message?: unknown; reason?: unknown; type?: unknown };
                return [candidate.message, candidate.reason, candidate.type]
                    .filter(Boolean)
                    .map((value) => String(value).toLowerCase())
                    .join(' ');
            }
            return String(error).toLowerCase();
        };

        const clearRuntimeCaches = async () => {
            if (!('caches' in window)) return;

            const keys = await caches.keys();
            await Promise.all(
                keys
                    .filter((key) => key.startsWith('aura-') || key.startsWith('youraura-'))
                    .map((key) => caches.delete(key))
            );
        };

        const recoverFromChunkError = async (error: unknown) => {
            const message = getErrorMessage(error);
            const isChunkError =
                message.includes('chunkloaderror') ||
                message.includes('loading chunk') ||
                message.includes('failed to load chunk') ||
                message.includes('loading css chunk') ||
                message.includes('failed to fetch dynamically imported module');

            if (!isChunkError) return;

            console.warn('Chunk load error detected, clearing runtime caches and reloading...', error);

            const lastReload = sessionStorage.getItem('chunk_reload_timestamp');
            const now = Date.now();

            if (!lastReload || now - parseInt(lastReload, 10) > 10000) {
                sessionStorage.setItem('chunk_reload_timestamp', now.toString());
                await clearRuntimeCaches();
                window.location.reload();
            }
        };

        const errorHandler = (event: ErrorEvent) => {
            recoverFromChunkError(event.error || event.message);
        };

        const rejectionHandler = (event: PromiseRejectionEvent) => {
            recoverFromChunkError(event.reason);
        };

        window.addEventListener('error', errorHandler);
        window.addEventListener('unhandledrejection', rejectionHandler);

        return () => {
            window.removeEventListener('error', errorHandler);
            window.removeEventListener('unhandledrejection', rejectionHandler);
        };
    }, []);

    return null;
}
